import { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { SwitchPricingModelModal } from './switch-pricing-model-modal';
import {
  ORG_PERMISSION_CONFIGS,
  OrgPermission,
  OrgPermissionConfig,
} from '../../../../../../../backend/src/organisation/organisation-permission';
import { PricingModel } from '../../../../../../../backend/src/types/types';
import { useSetOrganisationPermission } from '@/api/mutations/useSetOrganisationPermission';
import { useUpdateOrganisationBillingPeriod } from '@/api/mutations/useUpdateOrganisationBillingPeriod';
import { useUpdateOrganisationBlocking } from '@/api/mutations/useUpdateOrganisationBlocking';
import { useUpdateOrganisationIsTrialPackage } from '@/api/mutations/useUpdateOrganisationIsTrialPackage';
import { useUpdateSlackChannel } from '@/api/mutations/useUpdateSlackChannel';
import useGetOrganisation from '@/api/queries/useGetOrganisation';
import { dateToDatetimeLocal } from '@/helpers/formatDate';
import { useGlobalAdminCheck } from '@/hooks/useGlobalAdminCheck';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Switch } from '@/new/components/shadcn/switch';
import { SubTitle } from '@/new/components/subtitle';
import { TooltipIcon } from '@/new/components/tooltip-icon';

const isUsingOnboardingPackage = {
  id: 'onboarding-package-toggle',
  title: 'Onboarding Package',
  description:
    'Whether this organisation is currently on an onboarding package with increased usage limits. This will be indicated in the usage dashboard (and otherwise has no other effects).',
};

function PermissionRow({
  permission,
  isEnabled,
  onCheckedChange,
  disabled,
  isLoading = false,
}: {
  permission: Omit<OrgPermissionConfig, 'permission'>;
  isEnabled: boolean;
  onCheckedChange: (enabled: boolean) => void;
  disabled?: boolean;
  isLoading?: boolean;
}) {
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);

  return (
    <div className="flex max-w-4xl items-center gap-6">
      <Switch
        checked={isEnabled}
        disabled={disabled || isLoading}
        id={permission.id}
        onCheckedChange={(enabled) => setPendingEnabled(enabled)}
      />

      <div className="space-y-1">
        <p className="text-base font-medium text-gray-700">{permission.title}</p>
        <p className="text-gray-500">{permission.description}</p>
      </div>

      <ConfirmationModal
        isOpen={pendingEnabled !== null}
        onOpenChange={(open) => {
          if (!open) setPendingEnabled(null);
        }}
        title={pendingEnabled ? 'Enable permission' : 'Disable permission'}
        onConfirm={() => {
          if (pendingEnabled === null) return;
          onCheckedChange(pendingEnabled);
          setPendingEnabled(null);
        }}
        isLoading={isLoading}
      >
        <p>
          Are you sure you want to {pendingEnabled ? 'enable' : 'disable'} the{' '}
          <span className="font-semibold">{permission.title}</span> permission for this organisation?
        </p>
      </ConfirmationModal>
    </div>
  );
}

export function AdminManageOrganisation() {
  const { organisationId } = useParams();
  if (!organisationId) return null;

  useGlobalAdminCheck();

  const { data: organisation } = useGetOrganisation(organisationId);

  const { mutate: updateBlocking } = useUpdateOrganisationBlocking(organisationId);
  const { mutate: setOrganisationPermission, isLoading: isSettingPermission } =
    useSetOrganisationPermission(organisationId);
  const { mutate: updateBillingPeriod, isLoading: isUpdatingBillingPeriod } =
    useUpdateOrganisationBillingPeriod(organisationId);
  const { mutate: updateIsTrialPackage } = useUpdateOrganisationIsTrialPackage(organisationId);
  const { mutate: updateSlackChannel, isLoading: isUpdatingSlackChannel } = useUpdateSlackChannel(organisationId);

  const [maxCredits, setMaxCredits] = useState<number | undefined>();
  const [seatCount, setSeatCount] = useState<number | undefined>();
  const [billingStartDate, setBillingStartDate] = useState<Date | undefined>();
  const [billingEndDate, setBillingEndDate] = useState<Date | undefined>();
  const [billingDetailsChanged, setBillingDetailsChanged] = useState(false);
  const [slackChannelId, setSlackChannelId] = useState<string>('');
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [defaultCreditLimit, setDefaultCreditLimit] = useState<number | undefined>();

  const isSeatBased = organisation?.pricingModel === PricingModel.SeatBased;

  function setPermission(permission: OrgPermission, enabled: boolean) {
    if (!organisation) return;

    setOrganisationPermission({ permission, enabled });
  }

  useEffect(() => {
    setMaxCredits(organisation?.maxCredits ?? undefined);
    setSeatCount(organisation?.seatCount ?? undefined);
    setBillingStartDate(organisation?.billingStartDate ? new Date(organisation.billingStartDate) : undefined);
    setBillingEndDate(organisation?.billingEndDate ? new Date(organisation?.billingEndDate) : undefined);
    setSlackChannelId(organisation?.slackChannelId ?? '');
    setDefaultCreditLimit(organisation?.defaultCreditLimit ?? undefined);
  }, [organisation]);

  function handleBillingPeriodSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!billingStartDate || !billingEndDate) {
      toast.error(`Billing period dates can't be empty`);
      return;
    }

    if (isSeatBased) {
      if (seatCount === undefined) {
        toast.error(`Seat count can't be empty`);
        return;
      }
      if (defaultCreditLimit === undefined || defaultCreditLimit < 1) {
        toast.error(`Default credit limit can't be empty`);
        return;
      }
      updateBillingPeriod({
        seatCount,
        defaultCreditLimit,
        billingStartDate,
        billingEndDate,
      });
    } else {
      if (maxCredits === undefined) {
        toast.error(`Maximum credits can't be empty`);
        return;
      }
      updateBillingPeriod({
        maxCredits,
        billingStartDate,
        billingEndDate,
      });
    }
  }

  useEffect(() => {
    if (!organisation) return setBillingDetailsChanged(false);

    if (organisation.billingStartDate === null || organisation.billingEndDate === null) {
      return setBillingDetailsChanged(true);
    }

    const datesChanged =
      billingStartDate?.getTime() !== new Date(organisation.billingStartDate).getTime() ||
      billingEndDate?.getTime() !== new Date(organisation.billingEndDate).getTime();

    const configChanged = isSeatBased
      ? seatCount !== (organisation.seatCount ?? undefined) ||
        defaultCreditLimit !== (organisation.defaultCreditLimit ?? undefined)
      : maxCredits !== (organisation.maxCredits ?? undefined) || organisation.maxCredits === null;

    setBillingDetailsChanged(datesChanged || configChanged);
  }, [organisation, maxCredits, seatCount, defaultCreditLimit, billingStartDate, billingEndDate, isSeatBased]);

  return (
    <>
      <Breadcrumb
        items={
          organisation
            ? [
                { href: '/admin/organisations', label: 'Admin', active: false },
                {
                  href: `/admin/organisations/${organisation.id}`,
                  label: organisation.name,
                  active: true,
                },
              ]
            : [{ href: '/admin/organisations', label: 'Admin', active: false }]
        }
      />

      <PageContainer>
        {!organisation ? (
          // TODO: improve skeleton/move into generic component
          <div className="pulse my-8 h-8 w-sm rounded-lg bg-gray-50"></div>
        ) : (
          <>
            <PageHeader>
              <PageTitle text={organisation.name} />
            </PageHeader>

            <SubTitle text="Feature-flags" iconClass="lucide--flag" />

            <section className="my-8 flex flex-col gap-5">
              {ORG_PERMISSION_CONFIGS.map((permission) => (
                <PermissionRow
                  key={permission.id}
                  permission={permission}
                  isEnabled={organisation.permissions.includes(permission.permission)}
                  onCheckedChange={(enabled) => setPermission(permission.permission, enabled)}
                  isLoading={isSettingPermission}
                />
              ))}
            </section>

            <SubTitle text="Billing Information" iconClass="lucide--circle-dollar-sign" className="mt-10" />

            <div className="my-8 flex max-w-4xl items-center justify-between rounded-lg border border-gray-200 p-4 px-5">
              <div className="space-y-0.5">
                <p className="text-base font-medium text-gray-700">Pricing Model</p>
                <p className="text-sm text-gray-500">
                  Currently set to{' '}
                  <span className="font-semibold">
                    {organisation.pricingModel === PricingModel.SeatBased ? 'Seat-based' : 'Usage-based'}
                  </span>
                </p>
              </div>
              <Button onClick={() => setIsPricingModalOpen(true)}>
                <Icon className="lucide--arrow-left-right" />
                Switch
              </Button>
            </div>

            <SwitchPricingModelModal
              isOpen={isPricingModalOpen}
              onOpenChange={setIsPricingModalOpen}
              organisation={organisation}
            />
            {isSeatBased && (
              <div className="flex max-w-4xl gap-3 rounded-lg">
                <Icon className="lucide--info mt-0.5 h-5 w-5 shrink-0 text-gray-500" />
                <p className="text-sm leading-relaxed text-gray-600">
                  Per-user fair usage limits can be managed from the{' '}
                  <a
                    href={`/admin/organisations/${organisation.id}/users`}
                    className="font-medium text-gray-700 underline hover:text-gray-900"
                  >
                    Users
                  </a>{' '}
                  page for this organisation.
                </p>
              </div>
            )}
            <div className="my-8 flex flex-col gap-5">
              <PermissionRow
                permission={isUsingOnboardingPackage}
                isEnabled={organisation.isUsingTrialPackage}
                onCheckedChange={() => updateIsTrialPackage({ isUsingTrialPackage: !organisation.isUsingTrialPackage })}
              />
              <PermissionRow
                permission={{
                  id: 'blocking-toggle',
                  title: 'Blocking Enabled',
                  description:
                    'When enabled, users/organisations who exceed their usage limits will be blocked from further uploads and runs.',
                }}
                isEnabled={organisation.isBlockingEnabled}
                onCheckedChange={() =>
                  updateBlocking(
                    {
                      isBlockingEnabled: !organisation.isBlockingEnabled,
                    },
                    {
                      onSuccess: () => {
                        toast.success(
                          `Blocking ${organisation.isBlockingEnabled ? 'disabled' : 'enabled'} for this organisation`,
                        );
                      },
                    },
                  )
                }
              />
            </div>

            <form onSubmit={handleBillingPeriodSubmit} className="my-10 flex max-w-48 flex-col gap-3">
              {isSeatBased ? (
                <>
                  <div className="flex w-108 items-center">
                    <Label className="basis-1/2" htmlFor="seat-count">
                      Number of Seats
                    </Label>
                    <Input
                      className="basis-1/2"
                      type="number"
                      id="seat-count"
                      value={seatCount ?? ''}
                      onChange={(e) => setSeatCount(Number(e.target.value))}
                      disabled={isUpdatingBillingPeriod}
                      min="1"
                    />
                  </div>
                  <div className="flex w-108 items-center">
                    <Label className="flex basis-1/2 items-center gap-1.5" htmlFor="default-credit-limit">
                      Default Credit Limit
                      <TooltipIcon
                        tooltipContent="Applied to every current user in the org (resetting per-user overrides) and used as the starting limit for any new user added later."
                        triggerClassName="h-3.5 w-3.5"
                      />
                    </Label>
                    <Input
                      className="basis-1/2"
                      type="number"
                      id="default-credit-limit"
                      value={defaultCreditLimit ?? ''}
                      onChange={(e) => setDefaultCreditLimit(Number(e.target.value))}
                      disabled={isUpdatingBillingPeriod}
                      min="1"
                    />
                  </div>
                </>
              ) : (
                <div className="flex w-108 items-center">
                  <Label className="basis-1/2" htmlFor="max-credits">
                    Maximum Credits
                  </Label>
                  <Input
                    className="basis-1/2"
                    type="number"
                    id="max-credits"
                    value={maxCredits ?? ''}
                    onChange={(e) => setMaxCredits(Number(e.target.value))}
                    disabled={isUpdatingBillingPeriod}
                  />
                </div>
              )}

              <div className="flex w-108 items-center">
                <Label className="basis-1/2" htmlFor="start-date">
                  Billing Period Start Date
                </Label>
                <Input
                  className="basis-1/2"
                  type="datetime-local"
                  id="start-date"
                  value={billingStartDate ? dateToDatetimeLocal(billingStartDate) : ''}
                  onChange={(e) => setBillingStartDate(new Date(e.target.value))}
                  disabled={isUpdatingBillingPeriod}
                />
              </div>

              <div className="flex w-108 items-center">
                <Label className="basis-1/2" htmlFor="end-date">
                  Billing Period End Date
                </Label>
                <Input
                  className="basis-1/2"
                  type="datetime-local"
                  id="end-date"
                  value={billingEndDate ? dateToDatetimeLocal(billingEndDate) : ''}
                  onChange={(e) => setBillingEndDate(new Date(e.target.value))}
                  disabled={isUpdatingBillingPeriod}
                />
              </div>

              <Button
                variant="primary"
                type="submit"
                disabled={isUpdatingBillingPeriod || !billingDetailsChanged}
                className="my-3 w-fit"
              >
                <Icon className="lucide--banknote-arrow-up" />
                Update Billing Period
              </Button>
            </form>

            <SubTitle text="Settings" iconClass="lucide--message-square" className="mt-10" />

            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateSlackChannel(
                  { slackChannelId: slackChannelId.trim() },
                  {
                    onSuccess: () => {
                      toast.success('Slack channel updated');
                    },
                  },
                );
              }}
              className="my-8 flex flex-col gap-3"
            >
              <div className="flex w-108 items-center">
                <Label className="basis-1/2" htmlFor="slack-channel-id">
                  Slack Channel ID
                </Label>
                <Input
                  className="basis-1/2"
                  type="text"
                  id="slack-channel-id"
                  placeholder="e.g. C0123456789"
                  value={slackChannelId}
                  onChange={(e) => setSlackChannelId(e.target.value)}
                  disabled={isUpdatingSlackChannel}
                />
              </div>
              <p className="text-sm text-gray-500">
                Messages about runs and org events will be routed to this Slack channel. Leave empty to use the default
                monitoring channel.
              </p>
              <Button
                variant="primary"
                type="submit"
                disabled={isUpdatingSlackChannel || slackChannelId === (organisation?.slackChannelId ?? '')}
                className="my-3 w-fit"
              >
                <Icon className="lucide--save" />
                Update Slack Channel
              </Button>
            </form>
          </>
        )}
      </PageContainer>
    </>
  );
}
